<template>
	<Page>
		<BaseTypography tag="h1" weight="semi-bold" size="xxl">Swap</BaseTypography>

		<template v-if="aviableAssetAmounts && fromAssetAmount">
			<SelectAssetInputAmount
				v-model="fromAssetAmount"
				:assetAmounts="aviableAssetAmounts"
				:disableAutoMaxAmount="true"
			/>

			<div class="swap" @click="swapAssets"><ArrowSwitchHorizontalIcon /></div>

			<SelectAssetInputAmount
				v-model="toAssetAmount"
				:assetAmounts="aviableAssetAmounts"
				:disableInput="true"
				:disableAutoMaxAmount="true"
			/>

			<Section>
				<SectionLine>
					<div>Price</div>
					<div v-if="price">{{ price }}</div>
					<BaseSkeleton v-else class="text-skeleton" />
				</SectionLine>
				<SectionLine>
					<div>Price impact</div>
					<div v-if="aggregatorResult">{{ (aggregatorResult.priceImpact * 100).toFixed(4) }}%</div>
					<BaseSkeleton v-else class="text-skeleton" />
				</SectionLine>

				<SectionLine>
					<div>Minimum receive</div>

					<div v-if="aggregatorResult">{{ minReceiveFloat }}</div>
					<BaseSkeleton v-else class="text-skeleton" />
				</SectionLine>
				<SectionLine>
					<div>Slippage</div>
					<div>{{ slippagePercent }}%</div>
				</SectionLine>
			</Section>
		</template>

		<template #footer>
			<div v-if="error" class="destructive">Error: {{ error }}</div>
			<BaseButton :loading="isLoading" :disabled="!!error" @click="swap">Swap</BaseButton>
		</template>
	</Page>
</template>

<script setup lang="tsx">
import { watch, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import SelectAssetAmount from '@/components/ui/SelectAssetAmount.vue';
import { usePuzzleSwapApi } from '@/services/usePuzzlePyApi';
import { useBalanceStore } from '@/stores/useBalanceStore';
import Page from '@/components/ui/Page.vue';
import { AssetAmount } from '@/libs/asset-amount';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useAssets } from '@/services/useAssets';
import { usePromise } from '@/composables/usePromise';
import { assetIdFromString } from '@/utils';
import BaseSkeleton from '@/components/ui/BaseSkeleton.vue';
import { useStorage } from '@vueuse/core';
import { useSignTransactionsStore } from '@/stores/useSignTransactionsStore';
import { useWalletStore } from '@/stores/useWalletStore';
import { useProtectRoute } from '@/composables/useProtectRoute';
import SwapIcon from '../../components/icons/SwapIcon.vue';
import ButtonIcon from '@/components/ui/ButtonIcon.vue';
import SelectAssetInputAmount from '@/components/SelectAssetInputAmount.vue';
import Section from '@/components/ui/Section.vue';
import SectionLine from '@/components/ui/SectionLine.vue';
import ArrowSwitchHorizontalIcon from '@/assets/icons/arrow-switch-horizontal.svg?component';
import BaseTypography from '@/components/ui/BaseTypography.vue';

const START_FROM_ASSET_ID = null;
const START_INPUT_AMOUNT_FLOAT = 1;
const START_TO_ASSET_ID = 'G5WWWzzVsWRyzGf32xojbnfp7gXbWrgqJT8RcVWEfLmC';

const walletStore = useWalletStore();
const puzzleSwapApi = usePuzzleSwapApi();
const balanceStore = useBalanceStore();
const signTransactionsStore = useSignTransactionsStore();

useProtectRoute(() => {
	if (walletStore.chain !== 'W')
		useRouter().replace({
			path: '/unsupported',
			query: {
				message: 'swap available only in mainnet'
			}
		});
});

const availableAssetsIdsResponse = await puzzleSwapApi.getAvailableAssetsIds();
const aviableAssets = await useAssets(
	availableAssetsIdsResponse.availableAssetsIds.map((assetIdString) => assetIdFromString(assetIdString))
);
const aviableAssetAmounts = computed(() => {
	//sorted by usd value, then by owned, then others
	const ownedAssetAmounts: AssetAmount[] = [];
	const aviableAssetAmounts: AssetAmount[] = [];

	for (const asset of aviableAssets) {
		const owned = balanceStore.assetAmounts?.find((assetAmount) => assetAmount.asset.assetId === asset.assetId);
		if (owned) ownedAssetAmounts.push(owned);
		else aviableAssetAmounts.push(new AssetAmount(asset, 0));
	}

	return [...ownedAssetAmounts.sort((a, b) => b.inUsd() - a.inUsd()), ...aviableAssetAmounts];
});

const fromAssetAmount = ref(
	aviableAssetAmounts.value.find((assetAmount) => assetAmount.asset.assetId === START_FROM_ASSET_ID)
);
const toAssetAmount = ref(
	aviableAssetAmounts.value.find((assetAmount) => assetAmount.asset.assetId === START_TO_ASSET_ID)!
);
const aviableFromAssetAmount = computed(() => {
	return balanceStore.assetAmounts?.find((e) => e.asset.assetId === fromAssetAmount.value?.asset.assetId);
});

const maxAmountNumber = computed(() => {
	return (
		Number(
			aviableAssetAmounts.value.find((e) => e.asset.assetId === fromAssetAmount.value?.asset.assetId)?.amount
		) || 0
	);
});
const aviableAmountFloat = computed(() =>
	aviableAssetAmounts.value.find((e) => e.asset.assetId === fromAssetAmount.value?.asset.assetId)?.amountToFloat()
);

//TODO error catch
const {
	data: aggregatorResult,
	isLoading,
	execute
} = usePromise(
	() => {
		if (fromAssetAmount.value && toAssetAmount.value)
			return puzzleSwapApi.calc(
				fromAssetAmount.value.asset.key,
				toAssetAmount.value.asset.key,
				Number(fromAssetAmount.value.amount)
			);
		else return Promise.resolve(null);
	},
	null,
	{
		shallow: true,
		immediate: true
	}
);

watch(aggregatorResult, () => {
	if (!aggregatorResult.value) return;

	toAssetAmount.value = new AssetAmount(toAssetAmount.value.asset, aggregatorResult.value.estimatedOut);
});

const exceedsMaxBalanceError = computed(() => {
	if (!fromAssetAmount.value || !aviableFromAssetAmount.value) return false;

	return fromAssetAmount.value.amount > aviableFromAssetAmount.value.amount ? 'exceeds max balance' : false;
});

const error = computed(() => {
	return aggregatorResult.value?.error || exceedsMaxBalanceError.value;
});

const minReceive = computed(() =>
	aggregatorResult.value
		? Math.ceil(
				aggregatorResult.value.estimatedOut -
					(aggregatorResult.value.estimatedOut * slippage.value) / SLIPPAGE_DENOMINATOR
		  )
		: null
);
const minReceiveFloat = computed(() =>
	toAssetAmount.value && minReceive.value ? toAssetAmount.value.asset.amountToFloat(minReceive.value) : null
);

const price = computed(() => {
	return aggregatorResult.value && fromAssetAmount.value && toAssetAmount.value
		? (
				new AssetAmount(toAssetAmount.value?.asset, aggregatorResult.value.estimatedOut).amountToFloat() /
				new AssetAmount(fromAssetAmount.value?.asset, fromAssetAmount.value.amount).amountToFloat()
		  ).toPrecision(6)
		: null;
});

watch(fromAssetAmount, () => {
	execute();
});

watch(toAssetAmount, (newValue, oldValue) => {
	if (newValue.asset.assetId === oldValue.asset.assetId) return;

	execute();
});

const SLIPPAGE_DENOMINATOR = 10000;
const slippage = useStorage('swap-slippage', 100);
const slippagePercent = computed(() => ((slippage.value / SLIPPAGE_DENOMINATOR) * 100).toFixed(2));

const swap = () => {
	if (!fromAssetAmount.value) throw new Error('from asset amount not selected');
	if (!aggregatorResult.value) throw new Error('aggregator result not received');

	signTransactionsStore
		.sign(
			{
				type: 16,
				dApp: '3PGFHzVGT4NTigwCKP1NcwoXkodVZwvBuuU',
				call: {
					function: 'swapWithReferral',
					args: [
						{ type: 'string', value: aggregatorResult.value.parameters },
						{
							type: 'integer',
							value: minReceive.value!
						},
						{ type: 'string', value: 'wwbot' }
					]
				},
				payment: [
					{
						assetId: fromAssetAmount.value.asset.assetId,
						amount: String(fromAssetAmount.value.amount)
					}
				]
			},
			true
		)
		.catch();
};

const swapAssets = () => {
	const fromAssetId = fromAssetAmount.value?.asset.assetId;
	const toAssetId = toAssetAmount.value?.asset.assetId;

	fromAssetAmount.value = aviableAssetAmounts.value.find((assetAmount) => assetAmount.asset.assetId === toAssetId)!;
	toAssetAmount.value = aviableAssetAmounts.value.find((assetAmount) => assetAmount.asset.assetId === fromAssetId)!;
};
</script>

<style scoped>
.select-input-group {
	display: grid;
	grid-template-columns: 47.5% 47.5%;
	align-items: end;

	gap: 5%;
}

.bottom-list {
	display: flex;
	flex-direction: column;
	gap: var(--size-xxs);
	color: var(--hint-color);
}

.bottom-list .line {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	gap: var(--size-xl);
	text-align: justify;
}

.available {
	font-size: var(--size-s);
	margin-bottom: calc(0em - var(--size-xs));
}

.input-skeleton {
	padding: var(--size-s);
}

.text-skeleton {
	width: 7em;
}

.swap {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;

	width: 40px;
	height: 40px;

	margin: auto;

	background: rgba(255, 255, 255, 0.05);
	border-radius: 16px;

	stroke: rgba(255, 255, 255, 0.6);

	cursor: pointer;
}
</style>
